import type { FieldKey } from '@esign/contracts'

/** Nhãn tiếng Việt cho từng field. Một chỗ duy nhất — i18n sau này thay bằng lookup theo locale. */
export const FIELD_LABELS: Record<FieldKey, string> = {
  email: 'Email',
  phone: 'Số điện thoại nhận OTP',
  otp: 'Mã OTP',
  bank_account: 'Số tài khoản',
  bank_name: 'Ngân hàng',
  account_name: 'Tên chủ tài khoản',
  bank_branch: 'Chi nhánh',
  full_name: 'Họ và tên',
  id_no: 'Số CCCD',
  dob: 'Ngày sinh',
  id_issued_date: 'Ngày cấp',
  id_issued_place: 'Nơi cấp',
  tax_code: 'Mã số thuế',
  id_images: 'Ảnh giấy tờ',
  address: 'Địa chỉ',
}

/** Placeholder dùng chung để các flow/partner không tự đặt nội dung khác nhau. */
export const FIELD_PLACEHOLDERS: Partial<Record<FieldKey, string>> = {
  email: 'Nhập email',
  phone: 'Nhập số điện thoại',
  otp: 'Nhập mã OTP',
  bank_account: 'Nhập số tài khoản ngân hàng',
  bank_name: 'Nhập tên ngân hàng (ví dụ: TPBANK)',
  account_name: 'Nhập tên chủ tài khoản',
  bank_branch: 'Nhập chi nhánh ngân hàng',
  full_name: 'Nhập họ và tên',
  id_no: 'Nhập số CCCD',
  dob: 'DD/MM/YYYY',
  id_issued_date: 'DD/MM/YYYY',
  id_issued_place: 'Nhập nơi cấp',
  tax_code: 'Nhập mã số thuế',
  address: 'Nhập địa chỉ',
}

/**
 * Thông điệp cho `error_code` của BE.
 *
 * Không có case `default` sinh ra câu chung chung: code lạ thì hiện thẳng code.
 * Một mã lỗi trần trên màn hình là khó chịu nhưng debug được — "Đã có lỗi xảy ra"
 * thì không, và nó sẽ sống trong sản phẩm nhiều năm.
 */
export const ERROR_MESSAGES: Record<string, string> = {
  CONTRACT_CREATION_FAILED: 'Xác thực EKYC thành công nhưng tạo hợp đồng thất bại. Vui lòng thử lại sau.',
  CONTRACT_SIGN_FAILED: 'Ký hợp đồng không thành công. Vui lòng thử lại.',
  EKYC_SUBMIT_FAILED: 'Không thể gửi kết quả xác thực EKYC. Vui lòng thử lại.',
  IDENTITY_SUBMIT_FAILED: 'Không thể lưu thông tin định danh. Vui lòng thử lại.',
  FACE_SUBMIT_FAILED: 'Không thể lưu thông tin khuôn mặt. Vui lòng thử lại.',
  SESSION_EXPIRED: 'Phiên làm việc đã hết hạn. Vui lòng tải lại trang.',
  INVALID_TOKEN: 'Token không hợp lệ hoặc đã hết hạn.',
  INVALID_OTP: 'Mã OTP không đúng. Vui lòng kiểm tra lại.',
  ACCOUNT_NOT_MATCHED: 'Thông tin tài khoản không khớp với hồ sơ tại ngân hàng.',
  EKYC_DOCUMENT_MISSING: 'Chưa có đủ ảnh giấy tờ.',
  EKYC_SELFIE_MISSING: 'Chưa có ảnh chân dung.',
  STEP_OUT_OF_ORDER: 'Bước này không còn hợp lệ. Đang tải lại…',
  STEP_NOT_IN_FLOW: 'Bước này không thuộc quy trình của hợp đồng.',
  NETWORK_ERROR: 'Không thể kết nối dịch vụ ký hợp đồng. Vui lòng thử lại.',
  HTTP_ERROR: 'Dịch vụ ký hợp đồng đang tạm thời gián đoạn. Vui lòng thử lại.',
}

export const errorText = (code: string, data?: Record<string, unknown>): string => {
  const customMessage = data && typeof data['message'] === 'string' && data['message'].trim() ? data['message'] : null
  const standardMessage = ERROR_MESSAGES[code]

  if (customMessage) {
    return customMessage
  }

  if (standardMessage) {
    return standardMessage
  }

  return code
}

export const isValidEmail = (email?: string | null): boolean => {
  if (!email) return false
  const trimmed = email.trim()
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
}

export const isValidPhone = (phone?: string | null): boolean => {
  if (!phone) return false
  const cleaned = phone.trim().replace(/[\s.-]/g, '')
  return (
    /^(0|\+84|84)[3|5|7|8|9][0-9]{8}$/.test(cleaned) ||
    /^(0|\+?84)[0-9]{9}$/.test(cleaned) ||
    /^\+?[0-9]{9,15}$/.test(cleaned)
  )
}

export const isInfoReady = (email?: string | null, phone?: string | null): boolean =>
  isValidEmail(email) && isValidPhone(phone)

/** Giá trị prefill của form, phẳng hoá mảng (ảnh giấy tờ) thành chuỗi. */
export const initialValues = (subject: Record<string, string | string[]>): Record<string, string> =>
  Object.fromEntries(Object.entries(subject).map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : v]))

